import React from 'react';
import { useGetProcurementOrganizationSettingsQuery, useUpdateProcurementOrganizationSettingsMutation } from '../../store/endpoints/ProcurementEndpoints';
import { IProcurementSettings } from '../../models/Procurement';
import ToggleField from '../../components/Common/ToggleField';
import { DefaultButton, Stack } from '@fluentui/react';
import ContactSearchSelection from '../../components/Contacts/ContactSearchSelection';
import { IContact } from '../../models/Company';

const ProcurementManagementPage: React.FC = () => {
    const settings = useGetProcurementOrganizationSettingsQuery();
    const [saveSettings] = useUpdateProcurementOrganizationSettingsMutation();
    const [orgSettings, setOrgSettings] = React.useState<IProcurementSettings>();
    const [saving, setSaving] = React.useState(false);
    const [error, setError] = React.useState<string>();

    React.useEffect(() => {
        if (settings.data) {
            setOrgSettings(settings.data);
        }
    }, [settings.data]);

    const updateSettings = (updatedSettings: Partial<IProcurementSettings>) => {
        if (!orgSettings) return;
        setOrgSettings((oldSettings) => ({...oldSettings, ...updatedSettings}));
    }

    const save = async () => {
        if (!orgSettings) return;
        setSaving(true);
        setError(undefined);
        try {
            await saveSettings({ settings: orgSettings }).unwrap();
        } catch (e) {
            setError('Error saving settings');
        } finally {
            setSaving(false);
        }
    }

    const getContactPrimaryText = (contact: IContact) => {
        const nameParts = [];
        if (contact.firstName) nameParts.push(contact.firstName);
        if (contact.lastName) nameParts.push(contact.lastName);
        if (contact.companyDetails?.companyName) nameParts.push(`(${contact.companyDetails.companyName})`);
        return nameParts.join(' ');
    }
    const getContactSecondaryText = (contact: IContact) => {
        const addressParts = [];
        if (contact.companyDetails?.address?.address1) addressParts.push(contact.companyDetails.address.address1);
        if (contact.companyDetails?.address?.address2) addressParts.push(contact.companyDetails.address.address2);
        if (contact.companyDetails?.address?.city) addressParts.push(contact.companyDetails.address.city);
        if (contact.companyDetails?.address?.stateTerritory) addressParts.push(contact.companyDetails.address.stateTerritory);
        if (contact.companyDetails?.address?.postalCodeZip) addressParts.push(contact.companyDetails.address.postalCodeZip);
        return addressParts.join(' ');
    }

    return (
        <div>
            <h1>Procurement Management Settings</h1>
            <Stack tokens={{childrenGap: '1em'}}>
                <ToggleField
                    settingId='procurement.settings.enableManualItemProcurement'
                    label='Enable Manual Item Procurement'
                    checked={orgSettings?.enableManualItemProcurement || false}
                    onChange={(ev, checked) => {
                        updateSettings({ enableManualItemProcurement: checked || false });
                    }}
                />
                <ContactSearchSelection
                    label='Default Shipping Contact'
                    placeholder='Select Default Shipping Contact'
                    selectedContactId={orgSettings?.defaultShipmentContact ? [orgSettings.defaultShipmentContact] : undefined}
                    onContactSelected={(contactId) => {
                        updateSettings({ defaultShipmentContact: contactId[0] });
                    }}
                    getPrimaryText={getContactPrimaryText}
                    getSecondaryText={getContactSecondaryText}
                />
                <ContactSearchSelection
                    label='Shipment Contacts'
                    placeholder='Select Shipment Contacts'
                    selectedContactId={orgSettings?.shipmentContacts || []}
                    onContactSelected={(contactIds) => {
                        updateSettings({ shipmentContacts: contactIds });
                    }}
                    getPrimaryText={getContactPrimaryText}
                    getSecondaryText={getContactSecondaryText}
                    allowMultiple
                />
            </Stack>
            <DefaultButton
            style={{marginTop: '1em'}}
                primary
                text='Save'
                disabled={!orgSettings || saving}
                onClick={save}
                />
        </div>
    );
};

export default ProcurementManagementPage;